// 切换账户的按钮: 业务类型组件
import { useSWR } from '@tiger/swr';
import { Dropdown, Menu, MenuProps, Space } from 'antd';
import { observer } from 'mobx-react';
import { useCallback, useMemo, useState } from 'react';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import { ItemType } from 'antd/es/menu/hooks/useItems';
import Toast from 'components/Toast';
import { useCustomerInfo } from 'hooks/useCustomerInfo';
import { ReactComponent as Checked } from 'static/images/icons/checked-black.svg';
import APP from 'stores/APP';
import security from 'stores/security';
import { UserProcessStatus } from 'types/customer';
import { AccountClearingType, AccountCustomerTypeEnum, type AccountType } from 'types/user';
import API from 'utils/API';
import classlist from 'utils/classlist';
import HashLink from '../HashLink';
import { openNativeModal } from '../Modal';
import style from './style.scss';

interface AccountItem {
    ibAccount?: string;
    customerId?: number;
    accountType?: AccountType;
    category: 'IRA' | 'Individual';
    customerType: AccountCustomerTypeEnum;
    clearing?: AccountClearingType;
    tips?: string;
    // index 仅用于处理排序
    index: number;
}

const accountOpenTipsMap: Record<string, string> = {
    [AccountCustomerTypeEnum.INDIVIDUAL]: __('Fast account opening, support financing and convenient trading.'),
    [AccountCustomerTypeEnum.IRA_TRADITIONAL]: __(
        'A traditional IRA is a tax-advantaged personal savings plan where contributions may be tax deductible.'
    ),
    [AccountCustomerTypeEnum.IRA_ROTH]: __(
        'A Roth IRA is a tax-advantaged personal savings plan where contributions are not deductible but qualified distributions may be tax free.'
    ),
    [AccountCustomerTypeEnum.IRA_ROLLOVER]: __(
        'A rollover IRA is an account that allows you to move funds from an old retirement account into an IRA account, which helps to preserve the tax-deferred status of your retirement assets.'
    )
};

function getAccountName(data: AccountItem): string {
    if (data.category === 'IRA') {
        switch (data.customerType) {
            case AccountCustomerTypeEnum.IRA_TRADITIONAL:
                return __('Traditional IRA');
            case AccountCustomerTypeEnum.IRA_ROTH:
                return __('Roth IRA');
            case AccountCustomerTypeEnum.IRA_ROLLOVER:
                return __('Rollover IRA');
            default:
                return '';
        }
    }

    return data.clearing === 'ib' ? __('交易账户') : __('个人账户');
}

interface SwitchAccountButtonProps {
    className?: string;
    callback?: () => void; // 点击完切换的item项之后需要触发的回调函数
}

const SwitchAccountButton: React.FC<SwitchAccountButtonProps> = observer(({ className, callback }) => {
    const { switchAccountList = [], currentAccount } = security;
    const [open, setOpen] = useState(false);
    const isMobile = APP.isMobile || APP.isTiger;

    const { data: accountInfo } = useSWR<{
        account_infos: Array<{
            category: AccountItem['category'];
            reject_reason: string;
            br_id: string;
            status: UserProcessStatus;
            customer_type: AccountCustomerTypeEnum;
        }>;
    }>(API.CUSTOMER.fields.infos(), {
        dataChain: resp => resp.data
    });

    const rejectedCustomerTypes = useMemo<AccountCustomerTypeEnum[]>(() => {
        return (accountInfo?.account_infos || [])
            .filter(item => item.status === 'INVALID')
            .map(item => item.customer_type);
    }, [accountInfo]);

    const handleMenuItemClick = useCallback(
        async (account_type: AccountType, customer_id: number) => {
            Toast.loading(true, __('正在切换账户'));

            try {
                await API.OAUTH_CENTER.switchAccount.post({
                    account_type,
                    customer_id
                });

                callback?.();
                // 切换账户后，回到个人中心首页
                window.location.href = '/profile';
            } catch (error) {
                Toast.show(error);
            }

            Toast.loading(false);
        },
        [callback]
    );

    const handleMenuClick = useCallback(() => {
        APP.trackEvent('首页导航', '导航', '切换账户');
    }, []);

    /**
     * 开通 IRA 账户
     * @param type IRA 账户类型
     */
    const openIraAccount = useCallback(
        async (accountItem: AccountItem) => {
            if (switchAccountList.length === 0) {
                APP.updateHref(
                    `accounts/open?category=${accountItem.category}&customerType=${accountItem.customerType}`
                );
            } else {
                APP.updateHref(`accounts/upgrade/${accountItem.category}/${accountItem.customerType}`);
            }

            setOpen(false);
        },
        [switchAccountList]
    );

    const { data } = useCustomerInfo(security.currentCustomerId!);

    // 如果是非ssn 不能展示ira入口
    const isNotSSN = !!data?.id_type && data.id_type !== 'ssn_ids';

    /**
     * 账户切换列表，后端接口只返回有开户资料的账户（有customer_id）
     * 前端需要展示 individual 和 IRA 账户的开户入口
     * 展示顺序：ib 账户，individual 账户，Traditional IRA，Roth IRA，Rollover IRA
     * ib账户最多只有一个，也可能没有，未开户不展示入口
     * individual 账户可能有多个，未开户只展示一个入口
     * IRA 每种账户一个，未开户则展示开户入口
     * ib账户如果是已关户（status = 'UNACCOUNT' && customer_id）则不展示
     * 未开户的排在后面
     */
    const accountList: AccountItem[] = useMemo(() => {
        const list: AccountItem[] = [];

        // 需要处理的账户类型
        const accountTypes = ['ib', 'individual', 'traditional', 'roth', 'rollover'];
        // 各种类型的账户是否已经放到列表中
        const accountPushedMap: Record<string, boolean> = {};

        for (const accountType of accountTypes) {
            accountPushedMap[accountType] = false;
        }

        // 处理接口返回的账户列表
        for (const item of switchAccountList) {
            const { category, customerType, status, clearing } = item;
            let index = 100;

            if (category === 'Individual') {
                index += 10;

                if (clearing === 'ib') {
                    accountPushedMap.ib = true;
                    index += 1;

                    // ib 已关户的账户直接跳过，不放入列表中
                    if (status === 'UNACCOUNT') {
                        continue;
                    }
                } else {
                    accountPushedMap.individual = true;
                }

                list.push({ ...item, index });
            }

            if (category === 'IRA') {
                if (isNotSSN) {
                    continue;
                }

                switch (customerType) {
                    case AccountCustomerTypeEnum.IRA_TRADITIONAL:
                        accountPushedMap.traditional = true;
                        index += 3;
                        break;
                    case AccountCustomerTypeEnum.IRA_ROTH:
                        accountPushedMap.roth = true;
                        index += 2;
                        break;
                    case AccountCustomerTypeEnum.IRA_ROLLOVER:
                        accountPushedMap.rollover = true;
                        index += 1;
                        break;
                    default:
                        break;
                }

                list.push({ ...item, index });
            }
        }

        // 处理未开户的账户
        for (const accountType of accountTypes) {
            if (accountType === 'ib' || accountPushedMap[accountType]) {
                continue;
            }

            const customerTypeMap: Record<string, AccountCustomerTypeEnum> = {
                individual: AccountCustomerTypeEnum.INDIVIDUAL,
                traditional: AccountCustomerTypeEnum.IRA_TRADITIONAL,
                roth: AccountCustomerTypeEnum.IRA_ROTH,
                rollover: AccountCustomerTypeEnum.IRA_ROLLOVER
            };

            if (typeof customerTypeMap[accountType] === 'number') {
                const category = accountType === 'individual' ? 'Individual' : 'IRA';

                if (category === 'IRA' && isNotSSN) {
                    continue;
                }

                list.push({
                    category,
                    customerType: customerTypeMap[accountType],
                    tips: accountOpenTipsMap[accountType],
                    index: 0
                });
            }
        }

        return list.sort((prev, next) => next.index - prev.index);
    }, [switchAccountList, isNotSSN]);

    const getDropdownMenu: (close?: () => void) => MenuProps = useCallback(
        close => {
            const selectedKey = String(currentAccount?.customerId);

            // 切换账户按钮打开的下拉列表
            const accountLists: ItemType[] = accountList.map((item, index) => {
                const { customerId, accountType, customerType } = item;

                // 已开户的
                if (item.customerId) {
                    return {
                        key: index,
                        disabled: selectedKey === String(customerId),
                        className: style.menuItem,
                        onClick: () => handleMenuItemClick(accountType!, customerId!).then(close),
                        label: (
                            <div className={style.item}>
                                <span className={style.label}>
                                    {getAccountName(item)}
                                    {item.ibAccount ? `(${item.ibAccount})` : __('(开户中)')}
                                </span>
                                {selectedKey === String(customerId) ? (
                                    <Checked className={style.checked} />
                                ) : (
                                    rejectedCustomerTypes.includes(item.customerType) && (
                                        <HashLink className={style.accountTag}>
                                            <span className={style.redDot}>{__('Need Update')}</span>
                                            <RightOutlined />
                                        </HashLink>
                                    )
                                )}
                            </div>
                        )
                    };
                }

                // 未开户的
                return {
                    key: index,
                    className: style.menuItem,
                    style: { cursor: 'default' },
                    label: (
                        <div className={style.item}>
                            <div className={style.label}>
                                <span>{getAccountName(item)}</span>
                                <div className={style.tips}>{accountOpenTipsMap[customerType]}</div>
                            </div>
                            <button className={style.openBtn} onClick={() => openIraAccount(item).then(close)}>
                                <span>{__('开户')}</span>
                            </button>
                        </div>
                    )
                };
            });

            return {
                items: accountLists,
                selectedKeys: [selectedKey],
                onClick: handleMenuClick
            };
        },
        [accountList, currentAccount, rejectedCustomerTypes, handleMenuItemClick, handleMenuClick, openIraAccount]
    );

    const openSwitchModal = () => {
        if (!isMobile) {
            return;
        }

        openNativeModal({
            maskClosable: true,
            closable: true,
            width: '100%',
            component: props => (
                <div>
                    <h3 className={style.modalTitle}>{__('切换账户')}</h3>
                    <Menu className={style.modalMenu} {...getDropdownMenu(props.close)} />
                </div>
            )
        }).result.catch(() => {});
    };

    const showRedDot = rejectedCustomerTypes.some(item => item !== currentAccount!.customerType);

    return (
        <div className={classlist(style.root, { [style.redDot]: showRedDot }, className)}>
            <Dropdown
                arrow
                open={!isMobile && open}
                menu={getDropdownMenu()}
                onOpenChange={setOpen}
                overlayClassName={style.container}
                placement="bottomRight"
                trigger={['click']}>
                <Space className="text-link" align="start" onClick={openSwitchModal}>
                    {__('切换账户')}
                    <DownOutlined />
                </Space>
            </Dropdown>
        </div>
    );
});

export default SwitchAccountButton;
